import { getRiskLevel, getEffectivenessMultiplier } from './constants'

/**
 * Computes residual risk from inherent risk and control effectiveness
 * Formula from Plantilla ARI+RAT addval v1.1:
 * - multiplier(eff): 1→1.00, 2→0.80, 3→0.50, 4→0.20, 5→0.05
 * - P_res = max(1, round(P_inh × multiplier(prevEff)))
 * - I_res = max(1, round(I_inh × multiplier(corrEff)))
 * - risk_res = P_res × I_res
 * - level_res: same scale as inherent
 *
 * @param {number|null} P_inh - Inherent probability (1-5)
 * @param {number|null} I_inh - Inherent impact (1-5)
 * @param {number} prevEff - Preventive control effectiveness (1-5)
 * @param {number} corrEff - Corrective control effectiveness (1-5)
 * @returns {Object} { P_res, I_res, risk, level } or all null if insufficient data
 */
export function computeResidual(P_inh, I_inh, prevEff, corrEff) {
  // Validate inputs
  if (P_inh == null || I_inh == null || isNaN(P_inh) || isNaN(I_inh)) {
    return { P_res: null, I_res: null, risk: null, level: null }
  }

  // Default effectiveness to 1 (no control) if not provided
  const prevEffectiveness = prevEff ?? 1
  const corrEffectiveness = corrEff ?? 1

  // Get multipliers
  const prevMultiplier = getEffectivenessMultiplier(prevEffectiveness)
  const corrMultiplier = getEffectivenessMultiplier(corrEffectiveness)

  // Calculate residual P and I with minimum of 1
  const P_res = Math.max(1, Math.round(P_inh * prevMultiplier))
  const I_res = Math.max(1, Math.round(I_inh * corrMultiplier))

  // Calculate residual risk
  const risk = P_res * I_res

  // Determine level
  const level = getRiskLevel(risk)

  return { P_res, I_res, risk, level }
}
